/** @format */
import { rgba } from 'emotion-rgba';
import { css } from 'styled-components';

import { theme } from '@toucan/client/global/theme';

import { StyledPopoutMiniBasketProps } from './index';

/**
 * Base styles for the popout mini basket component
 *
 * @param {StyledPopoutMiniBasketProps} props The popout mini basket properties object
 * */
const baseStyles = ({
    lineItemsHeight,
    availableBasketHeight,
    lineItemsScrollHeight,
}: StyledPopoutMiniBasketProps) => {
    return css`
        position: relative;
        display: inline-block;

        .mini-basket-wrapper {
            min-width: 55rem;
            width: max-content;
            position: absolute;
            top: 100%;
            right: 50%;

            padding-top: 0.8rem;
            margin-right: -2.8rem;
        }

        .mini-basket {
            position: relative;
            border-radius: 0.8rem;
            background: var(--colors-surface-primary);
            box-shadow: var(--effects-shadow-base);
            max-height: ${availableBasketHeight / 10}rem;

            &::before {
                content: '';
                border-style: solid;
                border-width: 0 0.9rem 0.9rem 0.9rem;
                border-color: transparent transparent
                    var(--colors-border-inverse) transparent;
                position: absolute;
                bottom: 100%;
                right: 1.9rem;
            }

            .mini-basket-line-items {
                position: relative;

                ul {
                    max-height: ${lineItemsHeight / 10}rem;
                    margin: 0;
                    padding: 0;
                    overflow-y: ${lineItemsScrollHeight > lineItemsHeight
                        ? `scroll`
                        : `hidden`};

                    li {
                        box-shadow: none;
                        margin: 0;
                        border-radius: 0;
                        border-top: 0.1rem solid var(--colors-border-primary);

                        .content {
                            align-items: center;
                        }

                        .information {
                            align-items: center;
                        }

                        .title-text {
                            margin-bottom: var(--layout-spacing-4);
                        }

                        &:last-of-type {
                            border-bottom: 1px solid
                                var(--colors-border-primary);
                        }
                    }
                }
            }
        }

        .mini-basket-header {
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            padding: var(--layout-spacing-16) var(--layout-spacing-24);

            .mini-basket-title {
                margin-bottom: 0;
                align-self: center;
            }

            .mini-basket-close-button {
                align-self: center;
            }
        }

        .mini-basket-summary {
            background: inherit;
            box-sizing: border-box;
            width: 100%;
            border-bottom-left-radius: 0.8rem;
            border-bottom-right-radius: 0.8rem;

            .basket-error-notification {
                border-radius: 0;
            }

            .mini-basket-summary-content {
                display: flex;
                align-items: flex-end;
                padding: var(--layout-spacing-24);
                width: 100%;
            }

            .mini-basket-summary-total {
                position: relative;
                ${theme.typography.body_large};
                font-size: 2.1rem;
                color: var(--colors-text-secondary);
                margin: 0 var(--layout-spacing-24) 0 0;

                span {
                    position: absolute;
                    top: calc(var(--layout-spacing-12) * -1);
                    display: block;
                    ${theme.typography.body_small};
                }
            }

            .mini-basket-summary-cta {
                display: flex;
                justify-content: flex-end;
                align-items: center;
                flex-grow: 1;
                flex-shrink: 0;

                a:first-of-type {
                    margin-right: var(--layout-spacing-12);
                }
            }
        }
    `;
};

/**
 * Scroll styles for the mini basket component
 *
 * @param {StyledPopoutMiniBasketProps)} props The mini basket properties object
 */
const scrollStyles = ({
    lineItemsHeight,
    lineItemsScrollHeight,
    lineItemsAtScrollEnd,
}: StyledPopoutMiniBasketProps) => {
    //Show a 'fade' at the bottom of the line items when the list is scrollable.
    if (!lineItemsAtScrollEnd && lineItemsHeight <= lineItemsScrollHeight) {
        return css`
            .mini-basket-line-items:after {
                content: '';
                position: absolute;
                bottom: -1px;
                left: 0;
                width: calc(
                    100% - var(--layout-spacing-24)
                ); /* add spacing so scroll bar is not covered by the gradient */
                height: 7.2rem;
                margin-right: var(--layout-spacing-24);
                background: linear-gradient(
                    0deg,
                    white 0%,
                    ${rgba('white', 0)} 100%
                );
                pointer-events: none;
                z-index: 100;
                transition: opacity 140ms linear;
                opacity: 1;
            }
        `;
    }
    return css`
        .mini-basket-line-items:after {
            opacity: 0;
        }
    `;
};

/**
 * The popout mini basket component styles
 *
 * @param {PopoutMiniBasketProps} props The popout mini basket properties object
 * */
export const styles = (props: StyledPopoutMiniBasketProps) => {
    return css`
        ${baseStyles(props)}
        ${scrollStyles(props)}
    `;
};
